import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { changeAccountPassword } from '../js/accountService';
import { completeLocalLogout } from '../js/logout';
import { validateAccountSecurityForm } from '../utilities/accountSecurityValidation';

const INITIAL_SECURITY_FORM = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

export default function useSecurityPasswordForm({ onPasswordUpdated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(INITIAL_SECURITY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const reset = useCallback(() => {
    setForm(INITIAL_SECURITY_FORM);
    setErrors({});
  }, []);

  const openModal = useCallback(() => {
    reset();
    setOpen(true);
  }, [reset]);

  const closeModal = useCallback(() => {
    if (submitting) {
      return;
    }

    setOpen(false);
    reset();
  }, [reset, submitting]);

  const handleFieldChange = useCallback((field, value) => {
    setForm((prev) => {
      const nextForm = {
        ...prev,
        [field]: value,
      };

      setErrors((currentErrors) => {
        if (Object.keys(currentErrors).length === 0) {
          return currentErrors;
        }

        return validateAccountSecurityForm(nextForm);
      });

      return nextForm;
    });
  }, []);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    const nextErrors = validateAccountSecurityForm(form);

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);

    try {
      const result = await changeAccountPassword({
        currentPassword: form.currentPassword,
        password: form.newPassword,
        passwordConfirmation: form.confirmPassword,
      });

      toast.success(result.message);
      setOpen(false);
      reset();

      if (result.reauthRequired) {
        window.setTimeout(() => {
          completeLocalLogout();
        }, 1200);
        return;
      }

      await onPasswordUpdated?.();
    } catch (error) {
      toast.error(error.message || 'No se pudo actualizar la contraseña.');
    } finally {
      setSubmitting(false);
    }
  }, [form, onPasswordUpdated, reset]);

  return {
    open,
    form,
    errors,
    submitting,
    openModal,
    closeModal,
    handleFieldChange,
    handleSubmit,
  };
}